import * as React from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import HelpAdornment from './HelpAdornment';
import { createFilterOptions } from '@mui/material/Autocomplete';

const EMPTY_JOBS = Object.freeze([]); // stable fallback (no new [] each render)
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

// Filter keeps original sort order; searches label
const filterOptions = createFilterOptions({
  ignoreAccents: true,
  stringify: (option) => `${option.label}`,
});

export default function JobFilterSelect({
  label = "Jobs",
  error = false,
  required = false,
  fullWidth = false,
  size = "small",
  onChange,
  value = [],
  placeholder = "Select jobs...",
  hideHelpAdornment = false,
  helpKey
}) {
  // Raw list from Redux project (may be undefined/null before load)
  const jobsRaw = useSelector(state => state.project.single?.jobs);
  const baseJobs = jobsRaw ?? EMPTY_JOBS;

  // Filter active jobs and sort by label (asc), then id (asc) for stability
  const jobs = React.useMemo(() => {
    const copy = baseJobs
      .filter(job => job.active === true) // Only include active jobs
      .slice();
    copy.sort((a, b) => {
      const byLabel = collator.compare(a.label, b.label);
      if (byLabel !== 0) return byLabel;

      // Fallback to id comparison
      return (a.id ?? 0) - (b.id ?? 0);
    });
    return copy;
  }, [baseJobs]);

  // Convert selected IDs to job objects
  const selectedJobs = React.useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value
      .map(id => jobs.find(job => job.id === id))
      .filter(Boolean); // remove undefined
  }, [value, jobs]);

  // Handle selection change
  const handleChange = (event, newValue) => {
    const newIds = newValue ? newValue.map(job => job.id) : [];
    onChange?.(newIds);
  };

  return (
    <Autocomplete
      multiple
      fullWidth={fullWidth}
      size={size}
      options={jobs}
      value={selectedJobs}
      onChange={handleChange}
      getOptionLabel={(option) => option.label || `Job ${option.id}`}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={filterOptions}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.label || `Job ${option.id}`}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={selectedJobs.length === 0 ? placeholder : ''}
          error={error}
          required={required}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {params.InputProps.endAdornment}
                {!hideHelpAdornment && (
                  <HelpAdornment helpKey={helpKey} />
                )}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Typography variant="body2">
            {option.label || `Job ${option.id}`}
          </Typography>
        </li>
      )}
    />
  );
}